import type { ReactNode } from "react";
import { Routes, Route, Navigate, Link } from "react-router-dom";
import { CallFlow } from "../features/call/CallFlow";
import { Schedule } from "../features/schedule/Schedule";
import { AdminConsole } from "../features/admin/AdminConsole";
import { AdminSessionGate } from "../features/admin/AdminSessionGate";
import { ADMIN_PROTECTED_ROUTES } from "../features/admin/adminGate";
import { AvailabilityToggle } from "../features/workspace/AvailabilityToggle";
import { LanguageToggle } from "../i18n/LanguageToggle";
import { useI18n } from "../i18n/localization";
import { BrandTitle } from "./BrandTitle";

// Standalone shell — meetings + admin console navigation (docs/design/05-uiux-spec.md §4 + §6).
// Home is the public phone/OTP entry at "/", which is NOT rendered inside this shell; the
// former officer workstation (/station) is retired. Launch pages still render CallFlow from
// their injected bootstrap.
//
// Every route in ADMIN_PROTECTED_ROUTES (meetings + admin console) renders behind the
// administrator sign-in.
const protectedScreens: Record<typeof ADMIN_PROTECTED_ROUTES[number], ReactNode> = {
  "/schedule": <Schedule />,
  "/admin/*": <AdminConsole />
};

export function StandaloneShell() {
  const { t, dir } = useI18n();
  return (
    // Fills the area above the shared footer (AppRoot owns the viewport column, inlined so it
    // also holds without the extracted CSS). Matches the .vc-shell rule.
    <div
      className="vc-shell vc-shell--standalone"
      style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}
    >
      {/* Wraps on narrow screens: the navigation moves below the brand instead of crowding it. */}
      <header style={{ background: "#14305f", color: "#fff", padding: "10px 18px", display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px 16px", flex: "0 0 auto" }}>
        <BrandTitle />
        <nav style={{ marginInlineStart: "auto", fontSize: 13, display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
          <Link to="/" style={{ color: "#c4d2ea" }}>{t.nav.home}</Link>
          <Link to="/schedule" style={{ color: "#c4d2ea" }}>{t.nav.schedule}</Link>
          <Link to="/admin" style={{ color: "#c4d2ea" }}>{t.nav.admin}</Link>
          <AvailabilityToggle />
          <LanguageToggle />
        </nav>
      </header>
      {/* Routed content fills the height left by the header; min-height:0 lets the in-call
          screen shrink to fit while taller pages (schedule/admin/directory) scroll here. */}
      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "auto" }}>
        <Routes>
          {ADMIN_PROTECTED_ROUTES.map((path) => (
            <Route key={path} path={path} element={<AdminSessionGate>{protectedScreens[path]}</AdminSessionGate>} />
          ))}
          <Route path="/login" element={<Placeholder name="login" dir={dir} />} />
          {/* Launch pages (/vc/v1/launch) inject a bootstrap and render the officer call flow
              in place; anything else goes to the clean public root. */}
          <Route path="*" element={window.__VC_BOOTSTRAP__ != null ? <CallFlow /> : <Navigate to="/" replace />} />
        </Routes>
      </div>
    </div>
  );
}

function Placeholder({ name, dir }: { name: string; dir: "rtl" | "ltr" }) {
  const { t } = useI18n();
  return (
    <main dir={dir} style={{ padding: 24, fontFamily: "Segoe UI, Arial, sans-serif" }}>
      <h1 style={{ color: "#14305f", fontSize: 18 }}>{name}</h1>
      <p style={{ color: "#5b6b82", fontSize: 13 }}>
        {t.nav.placeholder}{" "}
        <code>docs/design/05-uiux-spec.md</code>.
      </p>
    </main>
  );
}
